import { ASSISTANT_ATTACHMENT_LIMITS, redactText } from "@cloudserver/contracts";
import { vncConnectionOf } from "../pages/server/console/connection.js";
import { rdpConnectionOf } from "../rdp/connection.js";
import { connectionOf } from "../terminal/connections.js";
import { shownTabs, terminals } from "../terminal/store.js";

/*
 * The screens the chat sees (docs/assistant.md, "Screen context"): the
 * terminals, remote desktops and consoles on screen in the session
 * workspace, and any screen a page registers. The focused terminal goes
 * along by itself while the Autopilot may at least read it (its access
 * level, `access.ts`; owner's design of 2026-10-22): with every message the
 * person sends, a snapshot — the screen and its last lines of scrollback,
 * redacted here and again by the API. Consoles and desktops have no level
 * yet and are not sent. Taken when the message is sent; nothing streams.
 */

export type ScreenKind = "terminal" | "console" | "desktop";

export type ScreenCapture =
  | { kind: "log"; text: string }
  | { kind: "image"; mediaType: "image/jpeg" | "image/png"; data: string };

export interface Screen {
  kind: ScreenKind;
  /** `<serverId>/<session>`: one chip per pane or console. */
  resourceId: string;
  projectId: string;
  serverId: string;
  serverName: string;
  capture: () => Promise<ScreenCapture | null>;
}

/** Lines of scrollback that go along with a terminal's screen. */
export const TERMINAL_SCROLLBACK = 200;
/**
 * The longest side of a picture sent (layer 3): a console's text stays
 * readable at 1024 px, and the picture costs about half the tokens of 1568.
 */
export const PICTURE_SIDE = 1024;
const JPEG_QUALITY = 0.72;
/** Lines of the last snapshot a new one must contain, in order, to send only what came after. */
const ANCHOR_LINES = 3;
/** A one-line anchor shorter than this (a bare prompt) could match anywhere. */
const MIN_ANCHOR_CHARS = 6;

/**
 * A terminal's snapshot as text: its lines with trailing blanks dropped,
 * the newest kept within `maxChars`, redacted.
 */
export function terminalSnapshot(
  lines: readonly string[],
  maxChars: number = ASSISTANT_ATTACHMENT_LIMITS.terminalChars,
): string {
  const trimmed = lines.map((line) => line.trimEnd());
  while (trimmed.length > 0 && trimmed.at(-1) === "") trimmed.pop();
  while (trimmed.length > 0 && trimmed[0] === "") trimmed.shift();
  let text = trimmed.join("\n");
  if (text.length > maxChars) {
    const cut = text.slice(text.length - maxChars);
    // Start on a whole line.
    const newline = cut.indexOf("\n");
    text = newline >= 0 ? cut.slice(newline + 1) : cut;
  }
  return redactText(text);
}

/** The size a picture is scaled to so its longer side is at most `side`. */
export function fitWithin(
  width: number,
  height: number,
  side: number = PICTURE_SIDE,
): { width: number; height: number } {
  const scale = Math.min(1, side / Math.max(width, height, 1));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** A canvas as a JPEG within the size caps, as base64; null when it is empty or too large. */
export async function canvasSnapshot(source: HTMLCanvasElement): Promise<ScreenCapture | null> {
  if (source.width === 0 || source.height === 0) return null;
  const size = fitWithin(source.width, source.height);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.drawImage(source, 0, 0, size.width, size.height);
  const blob = await new Promise<Blob | null>((done) =>
    canvas.toBlob(done, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob || blob.size > ASSISTANT_ATTACHMENT_LIMITS.imageBytes) return null;
  return { kind: "image", mediaType: "image/jpeg", data: await base64Of(blob) };
}

/** The bytes of a blob as base64. */
export async function base64Of(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

/**
 * Layer 3 of a terminal: only the lines that came since the last snapshot
 * sent. The last snapshot's lines before its last one (the cursor line,
 * which keeps changing) anchor the new one; what follows them is new. Null
 * when the anchor is not found (the screen was cleared, too much scrolled
 * by): then the whole snapshot goes again.
 */
export function newLines(previous: string, current: string): string | null {
  const before = previous.split("\n");
  before.pop();
  const lines = current.split("\n");
  // The longest anchor first; a single line only when it says something (not a bare prompt).
  for (let size = Math.min(ANCHOR_LINES, before.length); size >= 1; size -= 1) {
    const anchor = before.slice(-size);
    if (size === 1 && (anchor[0] ?? "").trim().length < MIN_ANCHOR_CHARS) break;
    for (let end = lines.length - 1; end >= size - 1; end -= 1) {
      const start = end - size + 1;
      if (anchor.every((line, i) => lines[start + i] === line))
        return lines.slice(end + 1).join("\n");
    }
  }
  return null;
}

/** A short fingerprint of a picture, to tell a changed console from the same one. */
export async function pictureHash(data: string): Promise<string> {
  const bytes = new TextEncoder().encode(data);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest).slice(0, 12)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** The number of lines of a selection, for its offer («Добавить выделенное (строк: N)»). */
export function lineCount(text: string): number {
  const trimmed = text.replace(/\n+$/, "");
  return trimmed ? trimmed.split("\n").length : 0;
}

/** The screens the workspace shows now: each pane's terminal or remote desktop. */
function workspaceScreens(): Screen[] {
  const state = terminals.snapshot();
  return shownTabs(state).flatMap((id) => screenOfTab(id));
}

/** The focused session's screen while the workspace is on screen and it is connected. */
export function activeScreen(): Screen | null {
  const state = terminals.snapshot();
  if (!state.visible || !state.active) return null;
  return screenOfTab(state.active)[0] ?? null;
}

/** The selected text of the focused terminal, when there is any. */
export function activeSelection(): { text: string; serverId: string; serverName: string } | null {
  const state = terminals.snapshot();
  const tab = state.visible ? state.tabs.find((t) => t.id === state.active) : undefined;
  if (!tab || (tab.kind ?? "ssh") !== "ssh") return null;
  const text = connectionOf(tab.id)?.copySelection() ?? "";
  return text.trim() ? { text, serverId: tab.serverId, serverName: tab.serverName } : null;
}

function screenOfTab(id: string): Screen[] {
  const state = terminals.snapshot();
  const tab = state.tabs.find((t) => t.id === id);
  if (!tab || tab.status !== "connected") return [];
  const base = {
    resourceId: `${tab.serverId}/${tab.id}`,
    projectId: tab.projectId,
    serverId: tab.serverId,
    serverName: tab.serverName,
  };
  // The console is a tab of the workspace too: one chip per server's console.
  if (tab.kind === "vnc")
    return [
      {
        ...base,
        resourceId: `${tab.serverId}/console`,
        kind: "console",
        capture: async () => {
          const canvas = vncConnectionOf(tab.id)?.snapshot();
          return canvas ? canvasSnapshot(canvas) : null;
        },
      },
    ];
  if (tab.kind === "rdp")
    return [
      {
        ...base,
        kind: "desktop",
        capture: async () => {
          const canvas = rdpConnectionOf(tab.id)?.snapshot();
          return canvas ? canvasSnapshot(canvas) : null;
        },
      },
    ];
  return [
    {
      ...base,
      kind: "terminal",
      capture: async () => {
        const shown = connectionOf(tab.id)?.screenLines(TERMINAL_SCROLLBACK);
        const text = shown ? terminalSnapshot(shown.lines) : "";
        return text ? { kind: "log", text } : null;
      },
    },
  ];
}

class ScreenRegistry {
  private readonly own = new Map<string, Screen>();
  private readonly listeners = new Set<() => void>();
  private shown = "";

  constructor() {
    // The workspace's panes and its focused tab change outside React: follow them.
    terminals.subscribe(() => {
      const shown = workspaceScreens()
        .map((s) => s.resourceId)
        .join(",");
      const now = `${shown}|${activeScreen()?.resourceId ?? ""}`;
      if (now !== this.shown) {
        this.shown = now;
        this.changed();
      }
    });
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private changed(): void {
    for (const listener of this.listeners) listener();
  }

  /** A page's own screen (the console) while it shows it; returns the way to take it back. */
  register(screen: Screen): () => void {
    this.own.set(screen.resourceId, screen);
    this.changed();
    return () => {
      if (this.own.get(screen.resourceId) === screen) this.own.delete(screen.resourceId);
      this.changed();
    };
  }

  /** Every screen on view now. */
  list(): Screen[] {
    return [...workspaceScreens(), ...this.own.values()];
  }

  find(resourceId: string): Screen | undefined {
    return this.list().find((s) => s.resourceId === resourceId);
  }
}

export const screens = new ScreenRegistry();
